import { Box } from '@deckee/deck-hand';
import React, { FC, useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import FloatingPillButton from '../../../../shared/FloatingPillButton/FloatingPillButton';
import { weatherDaySwipeIgnoreProps } from '../../helpers/day/daySwipe';
import {
  applyPlaybackSpeed,
  cyclePlaybackSpeed,
  getPlaybackSpeed,
} from './playbackSpeed';
import { SeaScene } from './SeaScene';
import type {
  ForecastAnimationPerformance,
  ForecastAnimationScene,
} from './types';

const Canvas = styled.canvas`
  position: relative;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
`;

/**
 * Own stacking context so a GPU-promoted canvas (especially inside the day
 * swipe track's `translate3d`) cannot paint over the speed control after the
 * first frame. That showed on iOS as a brief flash, then the pill gone.
 */
const AnimWrap = styled.div`
  position: relative;
  z-index: 0;
  isolation: isolate;
  width: 100%;
  height: 100%;
  transform: translateZ(0);
`;

const ForecastAnimationHost = styled.div.attrs({
  className: 'forecast-animation',
})`
  position: relative;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  transform: translateZ(0);
`;

/** Positioning lives on a slot, not the pill, so layout cannot be lost to canvas stacking. */
const SpeedControlSlot = styled.div`
  position: absolute;
  left: ${({ theme }) => `${theme.space[2]}px`};
  bottom: ${({ theme }) => `${theme.space[2]}px`};
  z-index: 2;
  pointer-events: auto;
  /* iOS WKWebView ignores z-index against a composited canvas in a 3D context. */
  transform: translate3d(0, 0, 1px);
`;

const SpeedControl = styled(FloatingPillButton)`
  svg {
    display: block;
    width: 11px;
    height: 11px;
    flex-shrink: 0;
  }
`;

const FastForwardIcon = (): JSX.Element => (
  <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M1 1.6 L6 6 L1 10.4 Z M6 1.6 L11 6 L6 10.4 Z"
    />
  </svg>
);

export interface ForecastAnimationProps extends ForecastAnimationScene {
  /** Canvas height in px. Defaults to the height the artwork was tuned at. */
  height?: number;
  /**
   * Called with a frame-time sample each frame. Use it to record the quality
   * ladder engaging on a real device; leave it off in production.
   */
  onPerformanceSample?: (sample: ForecastAnimationPerformance) => void;
  className?: string;
}

/** The height the v89 artwork and constants were tuned against. */
export const FORECAST_ANIMATION_HEIGHT = 166;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const readTimeScale = (host: HTMLElement): number => {
  const parsed = Number(host.getAttribute('speed'));

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

/**
 * The boating forecast sea animation.
 *
 * Standalone and fully props-driven: hand it one hour of conditions, a vessel, a
 * water body and the app's rating for that hour, and it draws that moment. It
 * holds no opinion about whether the conditions are safe — the hull rolls over
 * when `rating` is red and at no other time — so the animation can never
 * disagree with the colour shown elsewhere on the card.
 *
 * Everything expensive is deliberately kept out of React: the scene owns its own
 * canvas, frame loop and caches, and re-renders here only push new inputs into
 * it rather than tearing anything down. Playback speed is also kept off React
 * state — it lives in a shared module store and on the host `speed` attribute,
 * so changing days does not reset it and cycling it never disturbs the canvas.
 */
export const ForecastAnimation: FC<ForecastAnimationProps> = ({
  conditions,
  weatherCode,
  vessel,
  waterBody,
  rating,
  hour,
  height = FORECAST_ANIMATION_HEIGHT,
  onPerformanceSample,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<SeaScene | null>(null);
  const playbackSpeed = getPlaybackSpeed();

  // Kept in a ref so the scene can call the latest callback without the effect
  // below having to tear the scene down and rebuild it.
  const performanceCallbackRef = useRef(onPerformanceSample);
  performanceCallbackRef.current = onPerformanceSample;

  useLayoutEffect(() => {
    applyPlaybackSpeed(wrapRef.current, getPlaybackSpeed());
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;

    if (canvas == null || host == null) {
      return undefined;
    }

    let scene: SeaScene;

    try {
      scene = new SeaScene({
        canvas,
        reducedMotion: prefersReducedMotion(),
        onPerformanceSample: (sample) =>
          performanceCallbackRef.current?.(sample),
      });
    } catch {
      // No 2D context (very old browser, or canvas disabled). The card is still
      // fully usable without the animation, so fail quietly.
      return undefined;
    }

    sceneRef.current = scene;
    host.setAttribute('speed', String(getPlaybackSpeed()));
    scene.setTimeScale(readTimeScale(host));

    const applySpeedFromAttribute = (): void => {
      scene.setTimeScale(readTimeScale(host));
    };

    const speedObserver = new MutationObserver(applySpeedFromAttribute);
    speedObserver.observe(host, {
      attributes: true,
      attributeFilter: ['speed'],
    });

    const applySize = (): void => {
      const rect = canvas.getBoundingClientRect();
      scene.resize(rect.width, rect.height);
    };

    applySize();
    scene.start();

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(applySize)
        : null;
    resizeObserver?.observe(canvas);
    window.addEventListener('resize', applySize);

    // Battery saver: stop drawing entirely while the card is scrolled out of
    // view. The observer accounts for the clipping scroller, so this covers both
    // page scroll and any inner scroller.
    const intersectionObserver =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
            (entries) => scene.setVisible(entries[0].isIntersecting),
            { threshold: 0.01 },
          )
        : null;
    intersectionObserver?.observe(canvas);

    return () => {
      scene.stop();
      speedObserver.disconnect();
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      window.removeEventListener('resize', applySize);
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    sceneRef.current?.setScene({
      conditions,
      weatherCode,
      vessel,
      waterBody,
      rating,
      hour,
    });
  }, [conditions, weatherCode, vessel, waterBody, rating, hour]);

  return (
    <Box
      className={className}
      width="100%"
      height={`${height}px`}
      overflow="hidden"
    >
      <AnimWrap ref={wrapRef} data-anim-wrap="">
        <ForecastAnimationHost ref={hostRef}>
          <Canvas ref={canvasRef} aria-hidden="true" />
        </ForecastAnimationHost>
        <SpeedControlSlot
          data-testid="forecast-animation-speed-control"
          {...weatherDaySwipeIgnoreProps}
        >
          <SpeedControl
            data-speed={String(playbackSpeed)}
            aria-label={`Playback speed ${playbackSpeed}x`}
            onClick={cyclePlaybackSpeed}
          >
            <FastForwardIcon />
            <span data-speed-label="">{`${playbackSpeed}x`}</span>
          </SpeedControl>
        </SpeedControlSlot>
      </AnimWrap>
    </Box>
  );
};

export default ForecastAnimation;
